/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { ExplicitType } from 'd2/queries'
import { Flexbox } from 'd2/components/Layout'
import { memo } from 'react'
import { useIsPhone } from 'd2/components/Responsive'
import Body from 'd2/components/Typography/Body'
import CleanTag from 'd2/components/AudioTrackTags/CleanTag'
import ExplicitTag from 'd2/components/AudioTrackTags/ExplicitTag'
import FaIcon from 'd2/components/FaIcon'
import HorizontalSpacer from 'd2/components/HorizontalSpacer'
import IconButton from 'd2/components/ButtonV2/IconButton'
import LinkButton from 'd2/components/ButtonV2/LinkButton'
import Table from 'd2/components/Table'
import TrackMetadataModal from 'd2/components/TrackMetadataModal'
import Truncate from 'd2/components/Typography/Truncate'
import useModal from 'd2/hooks/useModal'
import useStyle from './styles'
import useTranslations from './translations'
import type { PlaylistColumns, Props } from './types'

const Playlist = memo<Props>(({
  currentSong,
  playing,
  playlistData,
  setCurrentSong,
  setPlaying,
  togglePlaying,
}) => {
  const isPhone = useIsPhone()
  const t = useTranslations()
  const { classes } = useStyle()
  const { hideModal, showModal } = useModal()

  const columns: PlaylistColumns = [
    {
      align: 'left',
      enableSorting: false,
      id: 'name',
      label: t.trackNameLabel,
      renderCellContents: function CellContents ({
        artistName,
        audioIsrc,
        collaboratingArtists,
        copyrightName,
        copyrightYear,
        explicitType,
        genre,
        label,
        lyrics,
        name,
        originalUrl,
        track,
      }) {
        return (<>
          <Flexbox inline>
            <Flexbox
              secondaryAlign='center'
            >
              <LinkButton
                onClick={() => { // eslint-disable-line react-memo/require-usememo
                  if (track !== currentSong?.track) {
                    setCurrentSong({
                      name,
                      src: originalUrl ?? undefined,
                      track,
                    })
                    setPlaying(true)
                    return
                  }
                  togglePlaying()
                }}
              >
                { track === currentSong?.track && playing
                  ? <FaIcon
                    icon='pause-circle'
                    weight='solid'
                  />
                  : <FaIcon
                    icon='play-circle'
                    weight='light'
                  /> }
              </LinkButton>
              <Body className={classes.trackNumber}>
                { `${track}.` }
              </Body>
            </Flexbox>
            <Flexbox
              secondaryAlign='center'
              style={{ minWidth: 0 }}
            >
              <Body>
                <Truncate title={name || t.untitled}>
                  <>
                    { name || t.untitled }
                    { explicitType === ExplicitType.explicit && <ExplicitTag /> }
                    { explicitType === ExplicitType.clean && <CleanTag /> }
                  </>
                </Truncate>
              </Body>
            </Flexbox>
            <Flexbox
              secondaryAlign='center'
              style={{ minWidth: 0 }}
            >
              <HorizontalSpacer half />
              <IconButton
                icon='expand'
                onClick={() => showModal(`TrackMetadataModal-${track}`)} // eslint-disable-line react-memo/require-usememo
                size='small'
                testID={track ? `Playlist-track-${track}-info` : 'Playlist-track-info'}
                weight='solid'
              >
                { t.details }
              </IconButton>
            </Flexbox>
          </Flexbox>
          <TrackMetadataModal
            artistName={artistName}
            audioIsrc={audioIsrc}
            collaboratingArtists={collaboratingArtists}
            copyrightName={copyrightName}
            copyrightYear={copyrightYear}
            genre={genre}
            hideModal={hideModal}
            label={label}
            lyrics={lyrics}
            modalIndex={`${track}`}
            trackName={name}
          />
        </>)
      },
      ...isPhone ? { width: '84%' } : { width: '88%' },
    },
    {
      align: 'right',
      enableSorting: false,
      id: 'duration',
      label: t.duration,
      ...isPhone ? { width: '16%' } : { width: '12%' },
    },
  ]

  return (
    <Table
      columns={columns}
      data={playlistData}
      noWrapper
      usePaper={false}
      withTopBorder
    />
  )
})

Playlist.displayName = 'Playlist'

export default Playlist
